import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import {
  CeoEmailMessageI,
  CeoEmailsMetaI,
  CeoQuickTemplateI,
  STORAGE_KEYS,
  ceoEmailError,
  ceoEmailStorage,
} from 'src/app/interfaces/ceo-emails.interface';
import { formatDate } from '../../ceo-theme';

/**
 * ⭐ "CONTACT" — a plain-text email from a real person to ONE customer.
 *
 * Opened from the customer panel. It starts on the quick-reply template used last
 * (remembered per browser), the text stays editable before sending, `{{name}}` is
 * filled per the backend's first-name rules, and the email goes out FROM and
 * REPLY-TO the chosen person, so the customer's answer lands in a real inbox.
 *
 * ⚠️ EDITING HERE NEVER CHANGES THE TEMPLATE unless "Update template" is pressed:
 * tweaking one message for one customer must not rewrite the canned reply for the
 * next one.
 */
@Component({
  selector: 'app-ceo-contact-modal',
  templateUrl: './ceo-contact-modal.component.html',
})
export class CeoContactModalComponent implements OnChanges {
  @Input() open = false;
  @Input() userId: string | null = null;
  @Input() userName = '';
  @Input() userEmail = '';
  @Input() unsubscribed = false;
  @Output() closed = new EventEmitter<void>();

  templates: CeoQuickTemplateI[] = [];
  /** Sender presets + the allowed reply-to list, shaped for `app-ceo-sender-picker`. */
  senderMeta: CeoEmailsMetaI | null = null;
  selectedTemplateId: string | null = null;
  message: CeoEmailMessageI = { mode: 'raw', fromEmail: '', fromName: '', replyToKey: '', subject: '', body: '' };

  loading = false;
  error = '';
  sending = false;
  confirming = false;
  sentTo = '';

  testOpen = false;
  testEmail = '';
  testing = false;
  testResult = '';

  savingTemplate = false;
  namingTemplate = false;
  newTemplateName = '';
  templateNotice = '';
  mobileTab: 'write' | 'preview' = 'write';

  history: { direct: any[]; campaigns: any[] } | null = null;

  readonly formatDate = formatDate;
  readonly nameToken = '{{name}}';

  constructor(private api: ApiSdkService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['userId']) && this.open && this.userId) {
      this.reset();
      this.load();
    }
  }

  private reset(): void {
    this.error = '';
    this.confirming = false;
    this.sentTo = '';
    this.testOpen = false;
    this.testResult = '';
    this.templateNotice = '';
    this.namingTemplate = false;
    this.mobileTab = 'write';
    this.history = null;
    this.testEmail = ceoEmailStorage.get(STORAGE_KEYS.testEmail) || '';
  }

  async load(): Promise<void> {
    this.loading = true;
    const userId = this.userId!;
    try {
      const [templatesResult, historyResult] = await Promise.all([
        this.api.listCeoQuickTemplates(),
        this.api.getCeoUserContactHistory(userId),
      ]);
      if (userId !== this.userId) return;
      const data = templatesResult?.success?.data;
      if (!data) {
        this.error = ceoEmailError(templatesResult, 'Could not load the quick replies');
        return;
      }
      this.templates = data.templates || [];
      this.senderMeta = {
        senderPresets: data.senderPresets || [],
        replyToOptions: data.replyToOptions || [],
        emailDomain: data.emailDomain || 'totalum.app',
      } as CeoEmailsMetaI;
      this.history = historyResult?.success?.data || null;

      const remembered = ceoEmailStorage.get(STORAGE_KEYS.lastQuickTemplate);
      const initial = this.templates.find(t => t._id === remembered) || this.templates[0] || null;
      if (initial) {
        this.applyTemplate(initial);
      } else {
        this.selectedTemplateId = null;
        this.message = { mode: 'raw', ...this.defaultSender(), subject: '', body: 'Hi {{name}},\n\n' };
      }
    } catch {
      this.error = 'Could not load the quick replies';
    } finally {
      this.loading = false;
    }
  }

  /** The template's own sender, else the one used last, else the first preset. */
  private defaultSender(template?: CeoQuickTemplateI): { fromEmail: string; fromName: string; replyToKey: string } {
    const presets = this.senderMeta?.senderPresets || [];
    if (template?.fromEmail) {
      return { fromEmail: template.fromEmail, fromName: template.fromName || '', replyToKey: template.replyToKey || '' };
    }
    const remembered = ceoEmailStorage.get(STORAGE_KEYS.lastSender);
    const preset = presets.find(p => p.email === remembered) || presets[0];
    const local = (preset?.email || '').split('@')[0];
    const canReply = (this.senderMeta?.replyToOptions || []).some(o => o.key === local);
    return { fromEmail: preset?.email || '', fromName: preset?.name || '', replyToKey: canReply ? local : '' };
  }

  applyTemplate(template: CeoQuickTemplateI): void {
    this.confirmDeleteTemplate = false;
    this.selectedTemplateId = template._id;
    this.templateNotice = '';
    this.message = { mode: 'raw', ...this.defaultSender(template), subject: template.subject, body: template.body };
  }

  get selectedTemplate(): CeoQuickTemplateI | undefined {
    return this.templates.find(t => t._id === this.selectedTemplateId);
  }

  /** The text differs from the template it came from. */
  get edited(): boolean {
    const t = this.selectedTemplate;
    return !!t && (t.subject !== this.message.subject || t.body !== this.message.body);
  }

  get lastContact(): { when: string; label: string } | null {
    const direct = this.history?.direct?.[0];
    const campaign = this.history?.campaigns?.[0];
    const latest = [direct && { at: direct.sentAt, label: `"${direct.subject}"` }, campaign && { at: campaign.sentAt, label: `campaign "${campaign.campaignName || campaign.subject}"` }]
      .filter(Boolean)
      .sort((a: any, b: any) => +new Date(b.at) - +new Date(a.at))[0] as any;
    return latest ? { when: formatDate(latest.at), label: latest.label } : null;
  }

  /** Where a reply would land, for the footer note. */
  get replyToEmail(): string {
    return (this.senderMeta?.replyToOptions || []).find(o => o.key === (this.message.replyToKey || ''))?.email || '';
  }

  get canSend(): boolean {
    return !!this.message.fromEmail && !!this.message.subject.trim() && !!this.message.body.trim();
  }

  insertName(textarea: HTMLTextAreaElement): void {
    const start = textarea.selectionStart ?? this.message.body.length;
    const end = textarea.selectionEnd ?? start;
    this.message.body = this.message.body.slice(0, start) + '{{name}}' + this.message.body.slice(end);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 8, start + 8);
    });
  }

  /** Start from scratch: the template is untouched, only this message is emptied. */
  clearText(textarea: HTMLTextAreaElement): void {
    this.message.body = '';
    setTimeout(() => textarea.focus());
  }

  confirmDeleteTemplate = false;

  async deleteTemplate(): Promise<void> {
    const template = this.selectedTemplate;
    if (!template) return;
    if (!this.confirmDeleteTemplate) {
      this.confirmDeleteTemplate = true;
      return;
    }
    this.confirmDeleteTemplate = false;
    const result = await this.api.deleteCeoQuickTemplate(template._id);
    if (!result?.success?.data) {
      this.templateNotice = ceoEmailError(result, 'Could not delete the template');
      return;
    }
    this.templates = this.templates.filter(t => t._id !== template._id);
    if (ceoEmailStorage.get(STORAGE_KEYS.lastQuickTemplate) === template._id) ceoEmailStorage.set(STORAGE_KEYS.lastQuickTemplate, '');
    // The text stays in the editor, now unattached, so nothing typed is lost.
    this.selectedTemplateId = null;
    this.templateNotice = 'Template deleted';
  }

  async sendTest(): Promise<void> {
    if (!this.testEmail.trim() || this.testing) return;
    this.testing = true;
    this.testResult = '';
    ceoEmailStorage.set(STORAGE_KEYS.testEmail, this.testEmail.trim());
    try {
      const result = await this.api.sendCeoTestEmail({ kind: 'direct', message: this.message, userId: this.userId, to: this.testEmail.trim() });
      this.testResult = result?.success?.data ? `Test sent to ${this.testEmail.trim()}` : ceoEmailError(result, 'The test could not be sent');
    } finally {
      this.testing = false;
    }
  }

  async send(): Promise<void> {
    if (!this.canSend || this.sending || !this.userId) return;
    if (!this.confirming) {
      this.confirming = true;
      return;
    }
    this.sending = true;
    this.error = '';
    try {
      const result = await this.api.contactCeoUser(this.userId, { ...this.message, templateId: this.selectedTemplateId });
      if (result?.success?.data) {
        if (this.selectedTemplateId) ceoEmailStorage.set(STORAGE_KEYS.lastQuickTemplate, this.selectedTemplateId);
        ceoEmailStorage.set(STORAGE_KEYS.lastSender, this.message.fromEmail);
        this.sentTo = result.success.data.to;
        setTimeout(() => this.close(), 1400);
      } else {
        this.error = ceoEmailError(result, 'The email could not be sent');
        this.confirming = false;
      }
    } finally {
      this.sending = false;
    }
  }

  async saveTemplate(asNew: boolean): Promise<void> {
    if (this.savingTemplate) return;
    if (asNew && !this.namingTemplate) {
      this.namingTemplate = true;
      this.newTemplateName = this.message.subject.slice(0, 60);
      return;
    }
    const name = (asNew ? this.newTemplateName : this.selectedTemplate?.name || '').trim();
    if (!name) return;
    this.savingTemplate = true;
    this.templateNotice = '';
    try {
      const body = {
        name, subject: this.message.subject, body: this.message.body,
        fromEmail: this.message.fromEmail, fromName: this.message.fromName, replyToKey: this.message.replyToKey,
      };
      const result = asNew || !this.selectedTemplateId
        ? await this.api.createCeoQuickTemplate(body)
        : await this.api.updateCeoQuickTemplate(this.selectedTemplateId, body);
      const saved: CeoQuickTemplateI = result?.success?.data;
      if (saved) {
        this.templates = [saved, ...this.templates.filter(t => t._id !== saved._id)];
        this.selectedTemplateId = saved._id;
        this.templateNotice = asNew ? 'Saved as a new quick reply' : 'Template updated';
        this.namingTemplate = false;
      } else {
        this.templateNotice = ceoEmailError(result, 'Could not save the template');
      }
    } finally {
      this.savingTemplate = false;
    }
  }

  close(): void {
    this.closed.emit();
  }
}
